/**
 * Incident Form Component
 * Form for creating and editing incidents
 */

import React, { useState } from 'react';
import type {
  CreateIncidentRequest,
  UpdateIncidentRequest,
  IncidentType,
  IncidentSeverity,
  IncidentStatus,
  Incident,
} from '../../types/incident';

interface IncidentFormProps {
  incident?: Incident;
  hotels: Array<{ id: string; name: string }>;
  onSubmit: (data: CreateIncidentRequest | UpdateIncidentRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const INCIDENT_TYPES: IncidentType[] = [
  'SECURITY_THREAT',
  'BEHAVIORAL_ISSUE',
  'PROPERTY_DAMAGE',
  'DOCUMENT_FRAUD',
  'OTHER',
];

const INCIDENT_SEVERITIES: IncidentSeverity[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

const INCIDENT_STATUSES: IncidentStatus[] = [
  'NEW',
  'UNDER_INVESTIGATION',
  'RESOLVED',
  'CLOSED',
];

const IncidentForm: React.FC<IncidentFormProps> = ({
  incident,
  hotels,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  // Fix: Initialize date in useEffect to avoid hydration mismatch
  const [formData, setFormData] = useState({
    hotelId: incident?.hotelId || '',
    type: incident?.type || ('SECURITY_THREAT' as IncidentType),
    severity: incident?.severity || ('MEDIUM' as IncidentSeverity),
    title: incident?.title || '',
    description: incident?.description || '',
    incidentDate: incident?.incidentDate
      ? new Date(incident.incidentDate).toISOString().split('T')[0]
      : '',
    involvedGuests: incident?.involvedGuests?.join(', ') || '',
    status: incident?.status || ('NEW' as IncidentStatus),
    assignedTo: incident?.assignedTo || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize date after mount if not editing
  React.useEffect(() => {
    if (!incident?.incidentDate) {
      setFormData(prev => ({
        ...prev,
        incidentDate: new Date().toISOString().split('T')[0]
      }));
    }
  }, [incident]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!incident && !formData.hotelId) {
      newErrors.hotelId = 'Veuillez sélectionner un hôtel';
    }
    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }
    if (!formData.incidentDate) {
      newErrors.incidentDate = 'La date de l\'incident est requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const data: CreateIncidentRequest | UpdateIncidentRequest = {
      ...(incident ? {} : { hotelId: formData.hotelId }),
      type: formData.type,
      severity: formData.severity,
      title: formData.title,
      description: formData.description,
      incidentDate: new Date(formData.incidentDate).toISOString(),
      involvedGuests: formData.involvedGuests
        ? formData.involvedGuests.split(',').map((g) => g.trim()).filter(Boolean)
        : [],
      ...(incident ? { status: formData.status } : {}),
      ...(formData.assignedTo ? { assignedTo: formData.assignedTo } : {}),
    };

    await onSubmit(data);
  };

  const formatLabel = (value: string): string => {
    return value
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Hotel Selection (only for new incidents) */}
      {!incident && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Hôtel <span className="text-red-500">*</span>
          </label>
          <select
            name="hotelId"
            value={formData.hotelId}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
              errors.hotelId ? 'border-red-500' : 'border-gray-300'
            }`}
            disabled={isLoading}
          >
            <option value="">Sélectionner un hôtel</option>
            {hotels.map((hotel) => (
              <option key={hotel.id} value={hotel.id}>
                {hotel.name}
              </option>
            ))}
          </select>
          {errors.hotelId && (
            <p className="mt-1 text-sm text-red-600">{errors.hotelId}</p>
          )}
        </div>
      )}

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Titre <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
            errors.title ? 'border-red-500' : 'border-gray-300'
          }`}
          disabled={isLoading}
          placeholder="Titre bref de l'incident"
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-600">{errors.title}</p>
        )}
      </div>

      {/* Type and Severity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type <span className="text-red-500">*</span>
          </label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            disabled={isLoading}
          >
            {INCIDENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {formatLabel(type)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Gravité <span className="text-red-500">*</span>
          </label>
          <select
            name="severity"
            value={formData.severity}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            disabled={isLoading}
          >
            {INCIDENT_SEVERITIES.map((severity) => (
              <option key={severity} value={severity}>
                {formatLabel(severity)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Incident Date and Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date de l'incident <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="incidentDate"
            value={formData.incidentDate}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
              errors.incidentDate ? 'border-red-500' : 'border-gray-300'
            }`}
            disabled={isLoading}
          />
          {errors.incidentDate && (
            <p className="mt-1 text-sm text-red-600">{errors.incidentDate}</p>
          )}
        </div>

        {incident && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Statut
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            >
              {INCIDENT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {formatLabel(status)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={4}
          className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
            errors.description ? 'border-red-500' : 'border-gray-300'
          }`}
          disabled={isLoading}
          placeholder="Description détaillée de l'incident"
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description}</p>
        )}
      </div>

      {/* Involved Guests */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Invités impliqués
        </label>
        <input
          type="text"
          name="involvedGuests"
          value={formData.involvedGuests}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          disabled={isLoading}
          placeholder="IDs des invités séparés par des virgules"
        />
        <p className="mt-1 text-xs text-gray-500">
          Entrez les IDs des invités séparés par des virgules
        </p>
      </div>

      {/* Assigned To */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Assigné à
        </label>
        <input
          type="text"
          name="assignedTo"
          value={formData.assignedTo}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          disabled={isLoading}
          placeholder="ID de l'utilisateur assigné"
        />
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          disabled={isLoading}
        >
          Annuler
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading ? 'Enregistrement...' : incident ? 'Mettre à jour' : 'Créer'}
        </button>
      </div>
    </form>
  );
};

export default IncidentForm;
